'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Button } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import Image from 'next/image';
import { Product } from '@benzinga/content-manager';
import { Impression, SmartLink } from '@benzinga/analytics';
import { AffiliateContext } from '../../context/AffiliateContext';

export interface PartnerProductsProps {
  products: Product[];
}

export const PartnerProducts: React.FC<PartnerProductsProps> = ({ products }) => {
  const { aff_id } = React.useContext(AffiliateContext);
  if (!Array.isArray(products)) return null;

  // const allProductAttributes = products.map(product => product.attributes ?? []);

  return (
    <Container>
      {products.map(product => {
        const impression_tag =
          product.tune_offer_id && aff_id
            ? `<img src="https://benzingapartners.go2cloud.org/aff_i?offer_id=${product.tune_offer_id}&aff_id=${aff_id}" width="0" height="0" style="position:absolute;visibility:hidden;" border="0" />`
            : null;
        return (
          <Impression
            campaign_id={`${product.id}`}
            tag={impression_tag ? impression_tag : undefined}
            unit_type="Display Product"
          >
            <PartnerProduct product={product} />
          </Impression>
        );
      })}
    </Container>
  );
};

const PartnerProduct = ({ product }) => {
  const buttonText = product?.button_text?.length > 1 ? product.button_text : 'Get Started';

  return (
    <PartnerProductWrapper>
      <SmartLink className="product-container" href={product.link} label="Partner Product">
        <div className="left-content">
          <div className="product-image-name">
            <div className="product-image">
              <Image alt={product.name} height={44} src={product.square_image ?? product.image} width={44} />
            </div>
            <div className="product-name">{product.name}</div>
          </div>
          <div className="product-teaser">{product.teaser}</div>
        </div>
        <div className="right-content cta-link-button">
          <Button variant="flat-orange">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(buttonText) }} />
          </Button>
        </div>
      </SmartLink>
    </PartnerProductWrapper>
  );
};

const PartnerProductWrapper = styled.div`
  background-color: ${({ theme }) => theme.colorPalette.white};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid #b8cbe6;
  box-shadow:
    0px 34px 14px rgba(139, 152, 162, 0.01),
    0px 19px 12px rgba(139, 152, 162, 0.05),
    0px 9px 9px rgba(139, 152, 162, 0.09),
    0px 2px 5px rgba(139, 152, 162, 0.1),
    0px 0px 0px rgba(139, 152, 162, 0.1);
  overflow: auto;
  font-size: 16px;
  margin: 1rem 0;

  .product-name {
    font-size: 26px;
    line-height: 30px;
  }

  a {
    color: black;
  }

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }

  .product-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
  }

  .product-image {
    min-width: 44px;
    margin-right: 1rem;
  }

  .product-image-name {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    font-weight: bold;
    line-height: 1.3rem;
  }

  .product-teaser {
    margin-top: 0.5rem;
    color: #555;
  }

  .left-content {
    min-width: 100px;
    padding-right: 0.5rem;
  }

  .cta-link-button {
    button {
      font-weight: 700;
      text-transform: uppercase;
      white-space: nowrap;
      width: 100%;
      min-width: 180px;
      border-radius: 0;
      padding: 0.8rem;
      line-height: 22px;
    }
  }

  @media screen and (max-width: 480px) {
    .right-content,
    .left-content {
      width: 100%;
    }
    .cta-link-button {
      margin-top: 0.5rem;
    }
    .product-container {
      flex-direction: column;
    }
  }
`;

const Container = styled.div`
  padding: 0 0.25rem;
`;
